// src/features/system/orchestrator/api/pullPackageApi.ts
//
// Frontend API layer for the async "Pull from Remote" job flow.
// Mirrors the BFF endpoints:
//   POST   /migrate/scopes/pull       → create job
//   GET    /migrate/scopes/pull       → list jobs
//   GET    /migrate/scopes/pull/:id   → poll / download
//   DELETE /migrate/scopes/pull/:id   → cancel / remove
import { bff } from '@/lib/http/bff';
import type { ScopeManifest } from './scopeMigrateApi';

// ── Types ───────────────────────────────────────────────────────────────────

export type PullJobStatus =
  | 'pending'
  | 'exporting'
  | 'ready'
  | 'failed'
  | 'expired';

export type PullJobStrategy = 'self' | 'remote';

/** Parameters sent to POST /pull to create a new job. */
export type PullJobParams = {
  root_scope_ids: number[];
  source_env: string;
  tenant_id?: number | null;
  include_outcomes?: boolean;
  include_hot_stream?: boolean;
  include_gcs_files?: boolean;
  include_archives?: boolean;
  max_depth?: number;
  /** Remote BFF origin — when set the pull strategy becomes "remote". */
  remote_url?: string | null;
  /** Bearer token for remote pull (required when remote_url is set). */
  access_token?: string | null;
};

/** 202 response from POST /pull. */
export type PullJobCreated = {
  id: string;
  status: PullJobStatus;
  strategy: PullJobStrategy;
  created_at: string;
};

/** Lightweight summary returned by GET /pull (list). */
export type PullJobSummary = {
  id: string;
  status: PullJobStatus;
  strategy: PullJobStrategy;
  progress: string | { phase?: string; target?: string; auth_method?: string } | null;
  created_at: string;
  finished_at: string | null;
  error: string | null;
  params: Omit<PullJobParams, 'access_token'>;
};

/** Full detail returned by GET /pull/:id when status === 'ready'. */
export type PullJobDetail = PullJobSummary & {
  manifest?: ScopeManifest;
};

// ── API calls ───────────────────────────────────────────────────────────────

const PULL_BASE = '/orchestrator/migrate/scopes/pull';

/** Create a new pull job (returns 202 with job metadata). */
export const createPullJob = (params: PullJobParams): Promise<PullJobCreated> =>
  bff.post<PullJobCreated>(PULL_BASE, params as any, { timeoutMs: 15_000 });

/** List all active pull jobs — metadata only (no manifest bodies). */
export const listPullJobs = (): Promise<PullJobSummary[]> =>
  bff.get<PullJobSummary[]>(PULL_BASE);

/**
 * Poll a single pull job.
 * Returns full manifest only when `status === 'ready'`.
 * For very large manifests, set a generous timeout.
 */
export const getPullJob = (id: string): Promise<PullJobDetail> =>
  bff.get<PullJobDetail>(`${PULL_BASE}/${id}`, { timeoutMs: 60_000 });

/** Cancel / remove a pull job. */
export const removePullJob = (id: string): Promise<{ status: string; id: string }> =>
  bff.del<{ status: string; id: string }>(`${PULL_BASE}/${id}`);
